import { useState, useEffect, useCallback } from 'react';
import DatabaseService from '../services/databaseService';

// Custom hook for book detail operations
export const useBookDetail = (bookId) => {
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to process media URLs (images and videos)
  const processMediaUrls = (imageUrls, videoUrls = null) => {
    const media = [];
    
    // Add images if they exist (handle both single URL and array)
    if (imageUrls) {
      const imageArray = Array.isArray(imageUrls) ? imageUrls : [imageUrls];
      imageArray.filter(Boolean).forEach(url => {
        media.push({
          type: 'image',
          url: url,
          alt: 'Imagen del producto'
        });
      });
    }
    
    // Add videos if they exist (handle both single URL and array)
    if (videoUrls) {
      const videoArray = Array.isArray(videoUrls) ? videoUrls : [videoUrls];
      videoArray.filter(Boolean).forEach(url => {
        media.push({
          type: 'video',
          url: url,
          alt: 'Video del producto'
        });
      });
    }
    
    // If no media, add default image
    if (media.length === 0) {
      media.push({
        type: 'image',
        url: "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=400&h=600&fit=crop",
        alt: 'Imagen por defecto'
      });
    }
    
    return media;
  };

  // Transform database book to match the expected format
  const transformBook = useCallback((dbBook) => {
    if (!dbBook) return null;

    // Process media (images and videos)
    const mediaItems = processMediaUrls(
      dbBook.image_url_product,
      dbBook.video_url_product
    );

    // Convert media items to the format expected by BookImageGallery
    const images = mediaItems.map(item => item.url);

    // Determine product status based on database fields
    const isAvailable = dbBook.available_product === true;
    const hasStock = (dbBook.stock_count_product || 0) > 0;
    const isOnSale = dbBook.on_sale_product === true;
    const inStock = isAvailable && hasStock;
    
    // Calculate product status for display
    let productStatus = 'Agotado';
    if (!isAvailable) {
      productStatus = 'No disponible';
    } else if (!hasStock) {
      productStatus = 'Agotado';
    } else if (isOnSale) {
      productStatus = 'En oferta';
    } else {
      productStatus = 'Disponible';
    }

    return {
      id: dbBook.id_product,
      title: dbBook.title_product,
      author: dbBook.authorName || dbBook.author_product || 'Autor no especificado',
      authorBiography: dbBook.authorBiography,
      originalPrice: dbBook.price_original_product,
      price: dbBook.price_original_product, // Always use original price
      rating: dbBook.rating_product || 4.5,
      reviewCount: dbBook.review_count_product || 0, // Use real review count from database
      inStock: inStock,
      stockCount: dbBook.stock_count_product || 0,
      isOnSale: isOnSale,
      isAvailable: isAvailable,
      available_product: dbBook.available_product, // Keep original field
      productStatus: isAvailable ? 'available' : 'unavailable', // New field for detailed status
      pages: dbBook.pages_product || null,
      language: dbBook.language_product || null,
      isbn: dbBook.isbn_product || null, // Use real ISBN if available
      dimensions: dbBook.dimensions_product || null,
      weight: dbBook.weight_product || null,
      publishDate: dbBook.publish_date_product ? new Date(dbBook.publish_date_product).toLocaleDateString('es-ES') : 
                   (dbBook.created_at_product ? new Date(dbBook.created_at_product).toLocaleDateString('es-ES') : null),
      categories: dbBook.categoryNames && dbBook.categoryNames.length > 0 ? dbBook.categoryNames : [],
      categoryIds: dbBook.categories_product || [],
      description: dbBook.description_product || "Descripción no disponible.",
      highlights: dbBook.highlights_product ? 
        (Array.isArray(dbBook.highlights_product) ? dbBook.highlights_product : [dbBook.highlights_product]) :
        [
          "Contenido de alta calidad",
          "Ideal para crecimiento espiritual",
          "Escrito por expertos en la materia",
          "Perfecto para estudio personal o grupal"
        ],
      featured_point_product: dbBook.featured_point_product ? 
        (Array.isArray(dbBook.featured_point_product) ? dbBook.featured_point_product : [dbBook.featured_point_product]) :
        [],
      images: images,
      mediaItems: mediaItems, // Keep original media items with type information
      isLiked: dbBook.favorite_product || false,
      // Generate realistic review data based on rating
      ratingDistribution: {
        5: Math.floor((dbBook.rating_product || 4.5) * 20),
        4: Math.floor((5 - (dbBook.rating_product || 4.5)) * 15),
        3: Math.floor(Math.random() * 10) + 5,
        2: Math.floor(Math.random() * 5) + 1,
        1: Math.floor(Math.random() * 3) + 1
      },
      reviews: [
        {
          userName: "Lector Verificado",
          rating: Math.min(5, Math.floor((dbBook.rating_product || 4.5) + 0.5)),
          date: "12/08/2024",
          title: "Excelente libro",
          comment: "Un libro que realmente impacta y transforma. Lo recomiendo ampliamente para cualquier persona que busque crecimiento espiritual.",
          helpfulCount: 23
        },
        {
          userName: "María González",
          rating: Math.min(5, Math.floor(dbBook.rating_product || 4.5)),
          date: "28/07/2024",
          title: "Muy recomendable",
          comment: "Las enseñanzas son claras y aplicables. Me ha ayudado mucho en mi crecimiento personal y espiritual.",
          helpfulCount: 18
        }
      ]
    };
  }, []);

  // Get book by ID using direct database query
  const fetchBook = useCallback(async (id) => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    try {
      // Use direct database query with author JOIN and categories
      const { data, error } = await DatabaseService.supabase
        .from('productbook')
        .select(`
          *,
          authorsbook(
            id_author,
            name_author
          )
        `)
        .eq('id_product', parseInt(id))
        .single();
      
      if (error) {
        throw error;
      }
      
      if (!data) {
        throw new Error('Libro no encontrado');
      }
      
      // Extract author name from authorsbook JOIN
      const authorName = data.authorsbook?.name_author || 'Autor desconocido';
      
      // Fetch category names if categories exist
      let categoryNames = [];
      if (data.categories_product && data.categories_product.length > 0) {
        const { data: categoriesData, error: categoriesError } = await DatabaseService.supabase
          .from('categoriesbook')
          .select('id_category, name_category')
          .in('id_category', data.categories_product);
        
        if (!categoriesError && categoriesData) {
          categoryNames = categoriesData.map(cat => cat.name_category);
        }
      }
      
      const mappedBook = {
        ...data,
        authorName: authorName,
        author: authorName,
        categoryNames: categoryNames
      };
      
      const transformedBook = transformBook(mappedBook);
      
      // DEBUG: Log transformed data
      console.log('✨ TRANSFORMED DATA:', {
        productStatus: transformedBook?.productStatus,
        inStock: transformedBook?.inStock,
        isAvailable: transformedBook?.isAvailable,
        author: transformedBook?.author,
        stockCount: transformedBook?.stockCount
      });
      
      setBook(transformedBook);
    } catch (err) {
      setError('Error al cargar el libro');
      console.error('❌ Error fetching book:', err);
    } finally {
      setLoading(false);
    }
  }, [transformBook]);

  // Toggle favorite
  const toggleFavorite = useCallback(async (isFavorite) => {
    if (!book) return false;
    
    try {
      const success = await DatabaseService.toggleFavorite(book.id, isFavorite);
      if (success) {
        setBook(prev => ({ ...prev, isLiked: isFavorite }));
      }
      return success;
    } catch (err) {
      console.error('Error toggling favorite:', err);
      return false;
    }
  }, [book]);

  // Get related books (books from same category)
  const fetchRelatedBooks = useCallback(async () => {
    if (!book || !book.categories || book.categories.length === 0) return [];
    
    try {
      // Get books from the first category
      const category = book.categories[0];
      const data = await DatabaseService.getBooksByCategory(category);
      
      // Filter out the current book and limit to 5
      const relatedBooks = data
        .filter(b => b.id_product !== book.id)
        .slice(0, 5)
        .map(dbBook => ({
          id: dbBook.id_product,
          title: dbBook.title_product,
          author: dbBook.author_product,
          originalPrice: dbBook.price_original_product,
          price: dbBook.price_original_product,
          rating: dbBook.rating_product || 4.5,
          reviewCount: 0,
          inStock: dbBook.available_product && dbBook.stock_count_product > 0,
          coverImage: dbBook.image_url_product || "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=300&h=400&fit=crop"
        }));

      return relatedBooks;
    } catch (err) {
      console.error('Error fetching related books:', err);
      return [];
    }
  }, [book]);

  // Force refresh function to bypass cache
  const refreshBook = useCallback(() => {
    if (bookId) {
      console.log('🔄 FORCING BOOK REFRESH for ID:', bookId);
      fetchBook(bookId);
    }
  }, [bookId, fetchBook]);

  // Load book when bookId changes
  useEffect(() => {
    if (bookId) {
      fetchBook(bookId);
    }
  }, [bookId, fetchBook]);

  return {
    book,
    loading,
    error,
    fetchBook,
    refreshBook,
    toggleFavorite,
    fetchRelatedBooks,
    clearError: () => setError(null)
  };
};

export default useBookDetail;

